const THUMBNAIL_MAX_SIZE = 1280;
const THUMBNAIL_JPEG_QUALITY = 0.88;

function waitForEvent(
  video: HTMLVideoElement,
  eventName: keyof HTMLVideoElementEventMap,
  timeoutMs = 8000,
) {
  return new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error(`Video ${eventName} timed out`));
    }, timeoutMs);

    function cleanup() {
      video.removeEventListener(eventName, onSuccess);
      video.removeEventListener("error", onError);
      window.clearTimeout(timeoutId);
    }

    function onSuccess() {
      cleanup();
      resolve();
    }

    function onError() {
      cleanup();
      reject(new Error(`Video ${eventName} failed`));
    }

    video.addEventListener(eventName, onSuccess, { once: true });
    video.addEventListener("error", onError, { once: true });
  });
}

async function seekTo(video: HTMLVideoElement, time: number) {
  const seekTime = Math.max(0, time);
  if (Math.abs(video.currentTime - seekTime) < 0.01) return;

  await new Promise<void>((resolve, reject) => {
    const onSeeked = () => {
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onError);
      resolve();
    };

    const onError = () => {
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onError);
      reject(new Error("Video seek failed"));
    };

    video.addEventListener("seeked", onSeeked);
    video.addEventListener("error", onError);
    video.currentTime = seekTime;
  });
}

export async function captureClipFrameBlob(video: HTMLVideoElement) {
  const { videoWidth, videoHeight } = video;
  if (!videoWidth || !videoHeight) {
    throw new Error("Missing video dimensions");
  }

  const scale = Math.min(
    1,
    THUMBNAIL_MAX_SIZE / Math.max(videoWidth, videoHeight),
  );
  const width = Math.max(1, Math.round(videoWidth * scale));
  const height = Math.max(1, Math.round(videoHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Missing canvas context");
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(video, 0, 0, width, height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to encode thumbnail"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      THUMBNAIL_JPEG_QUALITY,
    );
  });
}

async function captureMidpointFrame(video: HTMLVideoElement) {
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    await waitForEvent(video, "loadeddata");
  }

  const seekTime =
    video.duration && Number.isFinite(video.duration) ? video.duration / 2 : 0;

  try {
    await seekTo(video, seekTime);
  } catch {
    await seekTo(video, 0);
  }

  return captureClipFrameBlob(video);
}

export async function generateClipThumbnailBlob(file: File) {
  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = objectUrl;

  try {
    return await captureMidpointFrame(video);
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Captures a thumbnail from a remote clip URL (e.g. a presigned GET URL).
 * Requires `crossOrigin` so the frame can be drawn to a canvas without
 * tainting it; the source host must send permissive CORS headers, otherwise
 * the load fails and the caller falls back to an empty frame.
 */
export async function generateClipThumbnailBlobFromUrl(src: string) {
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = src;

  try {
    return await captureMidpointFrame(video);
  } finally {
    video.removeAttribute("src");
    video.load();
  }
}
